import type {MetricModel} from '../../../../../core/domain/Metric/Metric.model';
import type {Nullable} from '../../../../../utils/utility-types';
import type {Period} from '../../../types';

type Props = {
  metrics: MetricModel[];
  period: Period;
  unit: MetricModel.WeightUnit;
};

type Result = {
  data: Nullable<number>[];
  labels: string[];
  metricsByDateLabel: Map<string, MetricModel>;
};

const createDataByDays = ({metrics, period, unit}: Props): Result => {
  const supportedPeriods: Period[] = ['month', 'week'] as const;
  const DAYS_IN_WEEK = 7;
  const DAYS_IN_MONTH = 30;
  const FORMAT = 'MMM D';
  const lastDate = metrics[metrics.length - 1]?.date;

  if (!lastDate || !supportedPeriods.includes(period)) {
    return {data: [], labels: [], metricsByDateLabel: new Map()};
  }

  const daysInPeriod = period === 'month' ? DAYS_IN_MONTH : DAYS_IN_WEEK;
  const firstDate = lastDate.subtract(daysInPeriod - 1, 'day');
  const metricsByDateLabel = new Map<string, MetricModel>();

  metrics.forEach(metric => {
    metricsByDateLabel.set(metric.date.format(FORMAT), metric);
  });

  const labels = Array.from({length: daysInPeriod}, (_, index) => {
    return firstDate.add(index, 'day').format(FORMAT);
  });

  const data = labels.map(
    label => metricsByDateLabel.get(label)?.getByUnit(unit) ?? null,
  );

  return {data, labels, metricsByDateLabel};
};

const createDataByMonths = ({metrics, period, unit}: Props): Result => {
  const supportedPeriods: Period[] = ['half-year', 'year'] as const;
  const MONTHS_IN_YEAR = 12;
  const FORMAT = 'MMM';
  const lastDate = metrics[metrics.length - 1]?.date;

  if (!lastDate || !supportedPeriods.includes(period)) {
    return {data: [], labels: [], metricsByDateLabel: new Map()};
  }

  const monthsInPeriod =
    period === 'half-year' ? MONTHS_IN_YEAR / 2 : MONTHS_IN_YEAR;
  const firstMonth = lastDate.subtract(monthsInPeriod - 1, 'month');
  const metricsByDateLabel = new Map<string, MetricModel>();

  metrics.forEach(metric => {
    metricsByDateLabel.set(metric.date.format(FORMAT), metric);
  });

  const labels = Array.from({length: monthsInPeriod}, (_, index) => {
    return firstMonth.add(index, 'month').format(FORMAT);
  });

  const data = labels.map(
    label => metricsByDateLabel.get(label)?.getByUnit(unit) || null,
  );

  return {data, labels, metricsByDateLabel};
};

export const dataFactoriesByPeriod = {
  'half-year': createDataByMonths,
  month: createDataByDays,
  week: createDataByDays,
  year: createDataByMonths,
};
